'use client';

import { IProductDetail } from '@/api/types/public';
import Image from '@/components/Image';
import { Box } from '@mui/material';

interface PrescriptionDetailsProps {
  detail?: IProductDetail;
  variant?: 'default' | 'tab' | 'inPopup';
}

interface LensDetail {
  img: string;
  name: string;
  intro: string;
}

export default function PrescriptionDetails({
  detail,
  variant = 'default',
}: PrescriptionDetailsProps) {
  if (!detail?.lens_details?.length) {
    return null;
  }

  // 根据变体获取样式配置
  const getStyles = () => {
    if (variant === 'tab') {
      return {
        container: 'bg-[#f4f4f4] py-[3.75vw] px-[12.5vw] rounded-[4px] mt-[1.67vw]',
        grid: 'grid grid-cols-2 lg:grid-cols-3 gap-[40px]',
        item: 'flex items-start justify-between',
        imageContainer: 'flex items-center justify-center w-[64px] h-[64px] bg-white rounded-12',
        image: 'w-[48px] h-[48px] object-contain',
        textContainer: 'flex flex-col items-start justify-between ml-[16px] flex-1',
        title: 'font-bb text-16 leading-24 text-left',
        description: 'mt-8 font-normal text-14 text-[#808080] leading-[17px] text-left',
        imageSize: { width: 48, height: 48 },
      };
    }

    if (variant === 'inPopup') {
      return {
        container: '',
        grid: 'grid grid-cols-1 gap-20',
        item: 'flex justify-between items-start',
        imageContainer:
          'flex justify-center items-center w-[48px] h-[48px] rounded-[10px] bg-[#f4f4f4]',
        image: 'object-contain w-[32px] h-[auto]',
        textContainer: 'flex-1 ml-16',
        title: 'font-bb text-[13px] leading-[16px] text-left',
        description: 'mt-8 text-[12px] text-auxiliary-text leading-[20px] text-left font-ir',
        imageSize: { width: 32, height: 32 },
      };
    }

    return {
      container: '',
      grid: 'grid grid-cols-1 gap-20',
      item: 'flex justify-between items-start',
      imageContainer:
        'flex justify-center items-center w-[48px] h-[48px] bg-white rounded-[10px] border-1 border-[#d4d4d4]',
      image: 'object-contain w-[32px] h-[auto]',
      textContainer: 'w-[287px]',
      title: 'font-bb text-[13px] leading-[16px] text-left',
      description: 'mt-8 text-[13px] text-auxiliary-text leading-[20px] text-left font-ir',
      imageSize: { width: 32, height: 32 },
    };
  };

  const styles = getStyles();

  // 渲染单个镜片项目
  const renderLensItem = (item: LensDetail, index: number) => {
    const ItemContainer = variant === 'default' ? Box : 'div';
    const ImageContainer = variant === 'default' ? Box : 'div';

    return (
      <ItemContainer key={variant === 'default' ? item.name : index} className={styles.item}>
        <ImageContainer className={styles.imageContainer}>
          <Image
            src={item.img}
            alt={item.name}
            width={styles.imageSize.width}
            height={styles.imageSize.height}
            className={styles.image}
          />
        </ImageContainer>
        <div className={styles.textContainer}>
          <h4 className={styles.title}>{item.name}</h4>
          {variant === 'default' ? (
            <p className={styles.description}>{item.intro}</p>
          ) : (
            <div className={styles.description}>{item.intro}</div>
          )}
        </div>
      </ItemContainer>
    );
  };

  // 统一的渲染结构
  const GridContainer = variant === 'default' ? Box : 'div';

  return (
    <div className={styles.container}>
      <GridContainer className={styles.grid}>
        {detail.lens_details.map((item: LensDetail, index) => renderLensItem(item, index))}
      </GridContainer>
    </div>
  );
}
